import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, PhoneCall, Truck, Navigation } from "lucide-react";
import { CONTACT } from "../mock";
import { trackCall, sendContactMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "../hooks/use-toast";

const MAPS_HREF =
  CONTACT.mapsUrl ||
  `https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1) Persist in Neon
      await sendContactMessage(form);

      // 2) Netlify Forms → email notification (configure recipient in Netlify → Forms)
      const params = new URLSearchParams({
        "form-name": "contact",
        name: form.name,
        email: form.email,
        phone: form.phone || "",
        message: form.message,
      });
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      // 3) Direct mail bridge to inbox (browser → FormSubmit)
      try {
        await fetch(`https://formsubmit.co/ajax/${encodeURIComponent(CONTACT.email)}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            name: form.name,
            email: form.email,
            phone: form.phone || "—",
            message: form.message,
            _subject: `[DC Pièces] Message de ${form.name}`,
            _replyto: form.email,
            _template: "table",
          }),
        });
      } catch {
        /* Netlify Forms already queued */
      }

      toast({
        title: "Message envoyé",
        description: `Votre message a été envoyé à ${CONTACT.email}.`,
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast({
        title: "Envoi impossible",
        description: err?.response?.data?.detail || err.message || "Réessayez plus tard.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCallClick = () => {
    trackCall("contact");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold text-slate-900">Contactez-nous</h1>
        <p className="text-muted-foreground mt-2">
          Visitez notre magasin à Montréal, appelez-nous ou écrivez-nous — votre message arrive à{" "}
          <span className="font-medium text-slate-800">{CONTACT.email}</span>.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href={`tel:${CONTACT.phoneRaw}`} onClick={handleCallClick} className="inline-flex">
            <Button
              type="button"
              className="h-12 px-6 rounded-full bg-brand-orange hover:bg-orange-600 text-base gap-2 shadow-md"
            >
              <PhoneCall className="h-5 w-5" />
              Appeler {CONTACT.phone}
            </Button>
          </a>
          <a href={MAPS_HREF} target="_blank" rel="noopener noreferrer" className="inline-flex">
            <Button
              type="button"
              className="h-12 px-6 rounded-full bg-brand-blue hover:bg-blue-800 text-base gap-2"
            >
              <Navigation className="h-5 w-5" />
              Itinéraire / Maps live
            </Button>
          </a>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <div className="bg-white border border-border rounded-xl p-5 space-y-5">
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-brand-blue" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Adresse</p>
                <p className="text-slate-600">
                  {CONTACT.address}
                  <br />
                  {CONTACT.city}
                </p>
                <a
                  href={MAPS_HREF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-2 text-sm font-medium text-brand-blue hover:underline"
                >
                  Voir localisation maps live
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5 text-brand-blue" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Heures d&apos;ouverture</p>
                <p className="text-slate-600">{CONTACT.hours}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0">
                <Phone className="h-5 w-5 text-brand-blue" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Téléphone</p>
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  onClick={handleCallClick}
                  className="text-slate-600 hover:text-brand-blue"
                >
                  {CONTACT.phone}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="h-11 w-11 rounded-full bg-brand-blue/10 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-brand-blue" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">Courriel</p>
                <a href={`mailto:${CONTACT.email}`} className="text-slate-600 hover:text-brand-blue break-all">
                  {CONTACT.email}
                </a>
              </div>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 flex gap-3 text-sm text-amber-950">
              <Truck className="h-5 w-5 shrink-0 text-amber-700 mt-0.5" />
              <p>{CONTACT.deliveryNote}</p>
            </div>
          </div>

          <div className="rounded-xl overflow-hidden border border-border h-72 bg-slate-100">
            <iframe
              title="Localisation magasin — Google Maps live"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&output=embed&z=16`}
            />
          </div>
        </div>

        <form onSubmit={submit} className="bg-white border border-border rounded-xl p-6 h-fit">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Écrire un message</h2>
          <p className="text-sm text-muted-foreground mb-5">
            Envoyé à {CONTACT.email}
          </p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nom *</Label>
              <Input id="name" required value={form.name} onChange={set("name")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="cemail">Courriel *</Label>
              <Input id="cemail" type="email" required value={form.email} onChange={set("email")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="cphone">Téléphone</Label>
              <Input id="cphone" value={form.phone} onChange={set("phone")} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="cmsg">Message *</Label>
              <Textarea id="cmsg" required rows={5} value={form.message} onChange={set("message")} className="mt-1" />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-brand-orange hover:bg-orange-600 gap-2"
            >
              <Send className="h-4 w-4" /> {loading ? "Envoi…" : "Envoyer le message"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
